import React, { Component } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import max from 'lodash/max';
import min from 'lodash/min';
import flatten from 'lodash/flatten';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import ExportButton from 'components/Export';
import LineGraph from 'components/Charts/LineGraph';
import Select from 'components/Select';

const Container = styled.div`
  .card {
    height: calc(100% - 60px);
  }
  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .row > *:not(:last-child) {
    margin-right: 20px;
  }

  .recharts-cartesian-axis-tick {
    font-size: 12px;
    text {
      fill: rgb(102, 102, 102);
    }
  }
  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .rightComponentsContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    > :not(:last-child) {
      margin-right: 15px;
    }
  }
  .selectSetsContainer {
    width: 80%;
    margin: 2% auto;
  }
  .setListContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    margin-top: 5px;

    > div {
      margin: 0 !important;
      height: 30px !important;
      white-space: nowrap !important;
      width: initial !important;
    }
    .tag {
      width: initial !important;
    }
    > div:not(:last-child) {
      margin-right: 10px !important;
    }
  }

  .rhcTooltip {
    padding: 12px;
    min-width: 250px;
  }
  .rhcDate {
    color: #abb5be;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .rchLine {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  }

  .rchLabel {
    font-size: 12px;
    line-height: 18px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
  }

  .rchValue {
    font-size: 12px;
    line-height: 18px;
    margin-left: auto;
    font-family: ${(props) => props.theme.fonts.sfcompact_semibold};
  }

  .fixBug {
    margin-bottom: -6px;
    height: 5px;
    margin-top: 0px;
    z-index: 2;
    border-bottom: 1px solid rgb(228, 228, 228);
    width: calc(100% - 100px);
    margin-left: 75px;
    background-color: white;
  }

  @media (max-width: 1600px) {
    .fixBug {
      margin-top: 35px;
    }
  }

  .yAxis .recharts-cartesian-axis-tick:first-child {
    display: block !important;
  }
`;

class OnlineSalesDnLineChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tagList: []
    };
  }

  renderSelectSets() {
    const { rivalList, rivalSelection, handleRivalSelection } = this.props;

    return (
      <div className="selectSetsContainer ignored-export">
        <Select
          placeholder={I18n.t('placeholderRivals')}
          minWidth="170px"
          isMulti
          value={rivalSelection}
          dropdownProps={{
            styles: `margin-left:20px;margin-right:20px;`
          }}
          onChange={(value) => handleRivalSelection(value)}
          options={rivalList.map((item) => ({ value: item, label: item }))}
        />
      </div>
    );
  }

  sumData(data) {
    const averagedData = {};
    data.forEach((item) => {
      if (averagedData[item.date]) {
        Object.keys(averagedData[item.date]).forEach((key) => {
          if (key.includes('dn')) {
            averagedData[item.date][key] =
              averagedData[item.date][key] + item[key];
          }
        });
      } else {
        averagedData[item.date] = { ...item };
      }
    });
    return Object.values(averagedData);
  }

  renderTooltip(data, index) {
    return (
      <div className="rhcTooltip">
        <p className="rhcDate">
          {I18n.t('onlineSales.dnTooltipLabel')}{' '}
          {get(data, `[0][${index}].name`)}
        </p>
        {data
          .filter((set) => set[index] && set[index].value)
          .map((set) => set[index])
          .map((set, index) => (
            <div key={index} className="rchLine">
              <p className="rchLabel">{set.wine_name}</p>
              <p className="rchValue">{set.dn}</p>
            </div>
          ))}
      </div>
    );
  }

  formatData() {
    const {
      data = [],
      dataClient = [],
      rivalSelection,
      allMarket
    } = this.props;
    let formattedData = [
      allMarket ? this.sumData([...dataClient]) : [...dataClient]
    ];
    formattedData.sort(
      (a, b) =>
        moment(a.date).format('YYYYMMDD') - moment(b.date).format('YYYYMMDD')
    );
    if (rivalSelection && rivalSelection.length > 0) {
      const rivals = rivalSelection.map((item) => item.label);
      rivals.forEach((rival) => {
        const rivalData = allMarket
          ? this.sumData(data.filter((item) => item.wine_name === rival))
          : data.filter((item) => item.wine_name === rival);
        formattedData.push(rivalData);
      });
    }

    formattedData = formattedData.map((set) =>
      set.map((item) => ({
        ...item,
        name: moment(item.date).format('DD/MM/YYYY'),
        value: item.dn
      }))
    );
    let setNames = [];
    let wines = [];
    formattedData.forEach((set) => {
      if (set && set.length > 0) {
        set.sort(
          (a, b) =>
            Number(moment(a.name, 'DD/MM/YYYY').format('YYYYMMDD')) -
            Number(moment(b.name, 'DD/MM/YYYY').format('YYYYMMDD'))
        );
        setNames.push(...set.map((item) => item.name));
        wines.push(get(set, '[0].wine_name'));
      }
    });

    setNames = [...new Set(setNames)].sort(
      (a, b) =>
        Number(moment(a, 'DD/MM/YYYY').format('YYYYMMDD')) -
        Number(moment(b, 'DD/MM/YYYY').format('YYYYMMDD'))
    );
    formattedData.forEach((set, setIndex) => {
      setNames.forEach((name, index) => {
        if (!set[index] || set[index].name !== name) {
          set.splice(index, 0, {
            name,
            value: null,
            wine_name: wines[setIndex]
          });
        }
      });
    });
    return formattedData;
  }

  getLabels(data) {
    if (data && data.length > 0) {
      return data.map((set) => (set && set.length > 0 ? set[0].wine_name : ''));
    }
    return [];
  }

  getDataMax(data) {
    return max(
      flatten([...data].map((set) => set.map((item) => parseInt(item.dn, 10))))
    );
  }

  getDataMin(data) {
    return min(
      flatten([...data].map((set) => set.map((item) => parseInt(item.dn, 10))))
    );
  }

  render() {
    const { title, premium, exportOptions } = this.props;
    const formattedData = this.formatData();

    const labels = this.getLabels(formattedData);

    return (
      <Container>
        <Card className="card" id="vente-en-ligne-dn">
          <CardHeader
            alignItems="center"
            title={
              <div className="alignComponent">
                <p>{title}</p>
              </div>
            }
            styles={`.cardHeaderRight{width:100%; justify-content: flex-end;};flex-direction:row;align-items:center;`}
            fontSize="20px"
            iconRight="right"
            rightComponents={
              <ExportButton
                name={`${title}-${
                  get(exportOptions, 'market')
                    ? get(exportOptions, 'market')
                    : I18n.t('placeholderAll')
                }-${get(exportOptions, 'date')}-${get(
                  exportOptions,
                  'wine.label'
                )}`}
                jpg
                pdf
                targetElement="#vente-en-ligne-dn"
              />
            }
          />
          <div className="fixBug"></div>
          <LineGraph
            height={380}
            data={formattedData}
            customColors={[
              '#e25d34',
              'rgba(43, 61, 84,1)',
              'rgba(43, 61, 84,0.35)',
              'rgba(43, 61, 84,0.85)',
              'rgba(43, 61, 84,0.2)',
              'rgba(43, 61, 84,0.7)'
            ]}
            smallShape
            onlyCircle
            labels={labels}
            tooltipContent={(data, index) => this.renderTooltip(data, index)}
            yAxisProps={{
              scale: 'linear',
              type: 'number',
              allowDataOverflow: true,
              tickFormatter: (value) =>
                value.toLocaleString('fr-FR', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }),
              domain: [
                () => 0,
                (maxData) => Math.round(maxData + maxData / 3 + 5)
              ]
            }}
            tooltipOffset={{ x: -80, y: 20 + -20 * formattedData.length }}
            tooltipOffsetLeft={{ x: 0, y: -20 + -20 * formattedData.length }}
            tooltipOffsetRight={{
              x: -150,
              y: 40 + -20 * formattedData.length
            }}
            strokeDasharray={[null, ['5 5'], ['5 5']]}
          />

          {premium && this.renderSelectSets()}
        </Card>
      </Container>
    );
  }
}

export default OnlineSalesDnLineChart;
